import { Logger, buildLogger } from '@instinet-analytics/core-lib';
import { LoggingService } from '@instinet-analytics/core-lib';
import { PortfolioService } from 'portfolio/PortfolioService';
import { AggregationField } from 'widgets/portfolioSummary/aggregation/fields/aggregationFieldPortfolio';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { getAggregationStrategy } from 'widgets/portfolioSummary/aggregation/strategies/AggregationStrategyFactory';
import {
    computeFullAggregatedData,
    applyChanges,
    computeAggregatedDataDrilldown,
} from 'widgets/portfolioSummary/aggregation/portfolioSummaryDataAggregator';
import {
    portfolioSummarySetAggregatedDataAction,
    portfolioSummarySetDrilldownDataAction,
} from './actions';
import {
    PortfolioEventService,
    FilteredPortfolioChanged,
} from 'portfolio/PortfolioEventService';
import { selectAllWidgetsAggregationFields } from '../instances/configuration/selectors';
import { FilteredPortfolioChangedEventType } from 'portfolio/store/actions';
import {
    selectPortfolioSummaryAggregationFields,
    selectPortfolioSummaryAggregatedData,
} from './selectors';
import { PortfolioSummaryDrillDown } from 'widgets/portfolioSummary/store/instances/component/PortfolioSummaryComponentState';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import { selectAllDrillDowns } from 'widgets/portfolioSummary/store/instances/selectors';
import { IndigoStore } from 'store/store';
import {
    ActionsDeferredDispatcher,
    ActionsDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';
import { using as usingStatement } from 'using-statement';
import Measure from 'utils/measure/MeasureDecorator';

interface PortfolioSummaryDataServiceOptions {
    loggingService: LoggingService;
    portfolioService: PortfolioService;
    portfolioEventService: PortfolioEventService;
}

export class PortfolioSummaryDataService {
    private logger: Logger;
    private portfolioService: PortfolioService;
    public store: IndigoStore | undefined;

    public constructor(options: PortfolioSummaryDataServiceOptions) {
        this.logger = buildLogger(
            'PortfolioSummaryDataService',
            options.loggingService
        );
        this.portfolioService = options.portfolioService;

        options.portfolioEventService.subscribeFilteredPortfolioChanged(
            this.onFilteredPortfolioChangedAsync.bind(this)
        );
    }

    public recalculateAll() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const state = this.store.getState();

        const aggregationFields = selectAllWidgetsAggregationFields(state);

        this.calculateAggregatedData(aggregationFields);
    }

    @Measure()
    public calculateAggregatedData(aggregationFields: AggregationField[]) {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const state = this.store.getState();
        const appSettings = selectAppSettings(state);
        const { combineSellsAndShorts } = appSettings;

        const filteredPortfolio = this.portfolioService.getFilteredPortfolio();

        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                for (const aggregationField of aggregationFields) {
                    const strategy = getAggregationStrategy(
                        aggregationField,
                        combineSellsAndShorts
                    );

                    const aggregatedData = computeFullAggregatedData(
                        this.logger,
                        filteredPortfolio,
                        strategy
                    );
                    dispatcher.dispatch(
                        portfolioSummarySetAggregatedDataAction(
                            aggregationField,
                            aggregatedData
                        )
                    );
                }

                for (const aggregationFieldDrillDown of selectAllDrillDowns(
                    state
                )) {
                    this.drillDown(
                        filteredPortfolio,
                        combineSellsAndShorts,
                        aggregationFieldDrillDown.aggregationField,
                        aggregationFieldDrillDown.drillDown,
                        dispatcher
                    );
                }
            }
        );
    }

    private onFilteredPortfolioChangedAsync(ev?: FilteredPortfolioChanged) {
        setTimeout(() => this.onFilteredPortfolioChanged(ev), 0);
    }

    private onFilteredPortfolioChanged(ev?: FilteredPortfolioChanged) {
        if (!ev) {
            return;
        }

        if (ev.changedType === FilteredPortfolioChangedEventType.RESET) {
            this.recalculateAll();
            return;
        }

        if (!this.store) {
            throw new Error('store must be set');
        }

        if (ev.batchUpdateInProgress) {
            this.logger.debug(
                'Ignoring incremental change as batch update is in progress'
            );
            return;
        }

        const state = this.store.getState();

        const aggregationFields =
            selectPortfolioSummaryAggregationFields(state);

        const appSettings = selectAppSettings(state);
        const { combineSellsAndShorts } = appSettings;

        usingStatement(
            new ActionsDeferredDispatcher(this.store),
            (dispatcher) => {
                for (const aggregationField of aggregationFields) {
                    const strategy = getAggregationStrategy(
                        aggregationField,
                        combineSellsAndShorts
                    );

                    const aggregatedData = selectPortfolioSummaryAggregatedData(
                        state,
                        aggregationField
                    );

                    try {
                        const nextAggregatedData = applyChanges(
                            ev,
                            aggregatedData!,
                            strategy
                        );
                        dispatcher.dispatch(
                            portfolioSummarySetAggregatedDataAction(
                                aggregationField,
                                nextAggregatedData
                            )
                        );
                    } catch (err) {
                        this.logger.warn('Error applying changes', {
                            aggregationField,
                            aggregatedData,
                            err,
                        });
                    }
                }
            }
        );
    }

    public addDrilldown(
        aggregationField: AggregationField,
        drillDown: PortfolioSummaryDrillDown
    ) {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const state = this.store.getState();
        const appSettings = selectAppSettings(state);
        const { combineSellsAndShorts } = appSettings;

        const filteredPortfolio = this.portfolioService.getFilteredPortfolio();

        this.drillDown(
            filteredPortfolio,
            combineSellsAndShorts,
            aggregationField,
            drillDown
        );
    }

    private drillDown(
        filteredPortfolio: PortfolioItem[],
        combineSellsAndShorts: boolean,
        aggregationField: AggregationField,
        drillDown: PortfolioSummaryDrillDown,
        dispatcher: ActionsDispatcher = new ActionsImmediateDispatcher(
            this.store!
        )
    ) {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const aggregatedData = computeAggregatedDataDrilldown(
            this.logger,
            filteredPortfolio,
            combineSellsAndShorts,
            aggregationField,
            drillDown
        );

        dispatcher.dispatch(
            portfolioSummarySetDrilldownDataAction(
                aggregationField,
                drillDown,
                aggregatedData
            )
        );
    }
}
